import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { FileField, FileInput, SimpleForm, useNotify } from "react-admin";
import { useFormContext } from "react-hook-form";
import MuiButton from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useQuery } from "react-query";
import { useDataProvider } from "react-admin";
import { apiUrl } from "../../../dataProvider";

export default function DistributionPriceUploadDialog({ open, setOpen }) {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [distributionMode, setDistributionMode] = useState("none");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const { data: companies = [] } = useQuery(
    ["prices/distribution/companies"],
    () => dataProvider.getListNoParams("companies/select")
  );

  const PreviewSubmitButton = () => {
    const { getValues } = useFormContext();
    return (
      <MuiButton
        variant="outlined"
        type="button"
        disabled={loading}
        onClick={() => submit(getValues())}
      >
        {loading ? "Memeriksa..." : "Tampilkan Preview"}
      </MuiButton>
    );
  };

  const submit = async (values) => {
    const selected = values?.file;
    const file = (Array.isArray(selected) ? selected[0] : selected)?.rawFile || selected;
    if (!file) {
      notify("Pilih file Excel terlebih dahulu.", { type: "warning" });
      return;
    }
    setSelectedFile(file);
    setLoading(true);
    try {
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      const body = new FormData();
      body.append("file", file);
      body.append("distribution_mode", distributionMode);
      selectedCompanies.forEach((companyId) => body.append("target_company_ids", companyId));
      const response = await fetch(`${apiUrl}/prices/distribution/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "Preview upload gagal");
      setPreview(data);
      if (!data.valid) notify("Workbook memiliki error validasi.", { type: "warning" });
    } catch (error) {
      notify(error.message || "Preview upload gagal", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    if (!selectedFile || !preview?.valid) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      const body = new FormData();
      body.append("file", selectedFile);
      body.append("distribution_mode", distributionMode);
      selectedCompanies.forEach((companyId) => body.append("target_company_ids", companyId));
      const response = await fetch(`${apiUrl}/prices/distribution/execute`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "Proses harga gagal");
      notify(`Berhasil memproses ${data.processed || 0} harga.`, { type: "success" });
      setOpen(false);
    } catch (error) {
      notify(error.message || "Proses harga gagal", { type: "error" });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && setOpen(false)} fullWidth maxWidth="md">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DialogTitle>Preview Upload Distribusi Harga</DialogTitle>
        <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
      </Box>
      <DialogContent>
        <SimpleForm onSubmit={submit} toolbar={false} disabled={loading || processing}>
          <FileInput source="file" accept=".xlsx" label="Workbook harga distribusi">
            <FileField source="src" title="title" />
          </FileInput>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="distribution-mode-label">Distribusi</InputLabel>
            <Select labelId="distribution-mode-label" label="Distribusi" value={distributionMode} onChange={(event) => setDistributionMode(event.target.value)}>
              <MenuItem value="none">Tidak distribusi</MenuItem>
              <MenuItem value="all">Semua depo</MenuItem>
              <MenuItem value="selected">Pilih depo tertentu</MenuItem>
            </Select>
          </FormControl>
          {distributionMode === "selected" ? (
            <Autocomplete multiple options={companies} value={companies.filter((company) => selectedCompanies.includes(company.id))} onChange={(_, values) => setSelectedCompanies(values.map((value) => value.id))} getOptionLabel={(company) => company.alias || company.name || company.id} isOptionEqualToValue={(a, b) => a.id === b.id} renderInput={(params) => <TextField {...params} label="Depo target" placeholder="Pilih depo" />} />
          ) : null}
          <PreviewSubmitButton />
        </SimpleForm>
        {preview ? (
          <Box sx={{ mt: 2 }}>
            <Typography>Baris valid: {preview.row_count} | Company: {preview.company_count}</Typography>
            <Typography>Motif: {Object.keys(preview.motifs || {}).join(", ") || "-"}</Typography>
            {preview.adjustments?.length ? <Typography>Aturan motif terisi: {preview.adjustments.length}</Typography> : null}
            {preview.errors?.length ? (
              <Box sx={{ mt: 1, color: "error.main" }}>
                {preview.errors.slice(0, 20).map((error) => <Typography key={error} variant="body2">{error}</Typography>)}
              </Box>
            ) : <Typography color="success.main">Workbook valid untuk tahap preview.</Typography>}
            {preview.valid && distributionMode === "none" ? (
              <MuiButton sx={{ mt: 2 }} variant="contained" disabled={processing} onClick={execute}>
                {processing ? "Memproses..." : "Proses Harga"}
              </MuiButton>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
